import React, { useMemo, useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Typography,
} from '@mui/material';
import { Close, Add } from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import { convertToRoman } from 'utils/methods/math';

const MASK_TYPES = [
  { value: 'numerical', label: 'Numerical' },
  { value: 'alphabetical', label: 'Alphabetical' },
  { value: 'roman', label: 'Roman' },
];

function indexToAlphabetical(n) {
  let num = n;
  let result = '';
  while (num > 0) {
    num -= 1;
    result = String.fromCharCode(97 + (num % 26)).toUpperCase() + result;
    num = Math.floor(num / 26);
  }
  return result;
}

function collectUsedPages(rows, excludeId) {
  const used = new Set();
  rows.forEach((r) => {
    if (r.id === excludeId) return;
    for (let p = r.fromPage; p <= r.toPage; p += 1) {
      used.add(p);
    }
  });
  return used;
}

function freeIntervalsFromUsed(used, totalPages) {
  const out = [];
  let s = null;
  for (let p = 1; p <= totalPages; p += 1) {
    if (!used.has(p)) {
      if (s === null) s = p;
    } else if (s !== null) {
      out.push([s, p - 1]);
      s = null;
    }
  }
  if (s !== null) out.push([s, totalPages]);
  return out;
}

function freeIntervalsExcludingRow(rows, excludeId, totalPages) {
  return freeIntervalsFromUsed(collectUsedPages(rows, excludeId), totalPages);
}

function freeIntervalsAllRows(rows, totalPages) {
  return freeIntervalsFromUsed(collectUsedPages(rows, null), totalPages);
}

function clampRowToFreeIntervals(row, intervals) {
  const { fromPage: f, toPage: t } = row;
  for (const [s, e] of intervals) {
    if (f >= s && f <= e) {
      const hi = Math.min(t, e);
      if (hi >= f) return { ...row, fromPage: f, toPage: hi };
    }
  }
  for (const [s, e] of intervals) {
    if (s <= e) return { ...row, fromPage: s, toPage: e };
  }
  return { ...row, fromPage: 1, toPage: 1 };
}

function clampAllRows(rows, totalPages) {
  let next = rows.map((r) => ({ ...r }));
  for (let iter = 0; iter < next.length + 2; iter += 1) {
    let changed = false;
    next = next.map((row) => {
      const intervals = freeIntervalsExcludingRow(next, row.id, totalPages);
      const f = row.fromPage;
      const t = row.toPage;
      let inside = false;
      for (const [s, e] of intervals) {
        if (f >= s && f <= e && t >= s && t <= e && f <= t) {
          inside = true;
          break;
        }
      }
      if (inside) return row;
      const clamped = clampRowToFreeIntervals(row, intervals);
      if (clamped.fromPage !== row.fromPage || clamped.toPage !== row.toPage) {
        changed = true;
      }
      return clamped;
    });
    if (!changed) break;
  }
  return next;
}

function rangeTouchesOtherUsed(fromP, toP, otherUsed) {
  for (let p = fromP; p <= toP; p += 1) {
    if (otherUsed.has(p)) return true;
  }
  return false;
}

function sortedRanges(rows) {
  return rows;
  // return [...rows].sort((a, b) => a.fromPage - b.fromPage);
}

function validateFullCoverage(rows, totalPages) {
  if (!rows.length || totalPages < 1) return false;
  const sorted = sortedRanges(rows);
  const filled = sorted.filter((r) => r.type);
  if (filled.length !== rows.length) return false;
  if (filled[0].fromPage !== 1) return false;
  if (filled[filled.length - 1].toPage !== totalPages) return false;
  for (let i = 0; i < filled.length; i += 1) {
    if (filled[i].toPage < filled[i].fromPage) return false;
    if (i > 0 && filled[i].fromPage !== filled[i - 1].toPage + 1) return false;
  }
  const types = filled.map((r) => r.type);
  if (new Set(types).size !== types.length) return false;
  return true;
}

function buildUpdates(rows, pages) {
  const totalPages = pages?.length || 0;
  const sorted = sortedRanges(rows);
  const out = [];
  for (let i = 0; i < sorted.length; i += 1) {
    const row = sorted[i];
    if (!row.type) continue;
    const fromP = row.fromPage;
    const toP = row.toPage;
    for (let p = fromP; p <= toP; p += 1) {
      const page = pages[p - 1];
      if (!page) continue;
      const localIndex = p - fromP + 1;
      let mask = '';
      if (row.type === 'numerical') {
        mask = String(localIndex);
      } else if (row.type === 'alphabetical') {
        mask = indexToAlphabetical(localIndex);
      } else if (row.type === 'roman') {
        mask = convertToRoman(localIndex);
      }
      out.push({ pageId: page.pageId, NumberMasked: mask });
    }
  }
  if (out.length !== totalPages) {
    return [];
  }
  return out;
}

const isNumeric = (v) => /^[0-9]+$/.test(v);
const isAlphabet = (v) => /^[A-Za-z]$/.test(v);
const isRomanChar = (v) => /^[IVXLCDM]+$/i.test(v);

const romanToNumber = (roman) => {
  const map = { I: 1, V: 5, X: 10, L: 50, C: 100 };
  let num = 0;
  for (let i = 0; i < roman.length; i++) {
    const curr = map[roman[i]];
    const next = map[roman[i + 1]];
    if (next > curr) num -= curr;
    else num += curr;
  }
  return num;
};

const isRomanSeq = (prev, curr) =>
  isRomanChar(prev) &&
  isRomanChar(curr) &&
  romanToNumber(curr) === romanToNumber(prev) + 1;

const buildRanges = (arr) => {
  if (!arr.length) return [];

  const result = [];

  let startIndex = 0;
  let prevVal = arr[0].NumberMasked;

  let prevType = isNumeric(prevVal)
    ? 'numerical'
    : isRomanChar(prevVal) && isRomanChar(arr[1]?.NumberMasked)
    ? 'roman'
    : 'alphabetical';

  for (let i = 1; i < arr.length; i++) {
    const curr = arr[i];
    const currVal = curr.NumberMasked;

    let currType = prevType;
    if (isNumeric(currVal)) {
      currType = 'numerical';
    } else if (isRomanChar(currVal) && (currVal === 'I' || (prevType === 'roman' && isRomanSeq(prevVal, currVal)))) {
      currType = 'roman';
    } else {
      currType = 'alphabetical';
    }

    if (currType !== prevType) {
      result.push({
        id: uuidv4(),
        fromPage: startIndex + 1,
        toPage: i,
        type: prevType
      });

      startIndex = i;
    }

    prevVal = currVal;
    prevType = currType;
  }

  result.push({
    id: uuidv4(),
    fromPage: startIndex + 1,
    toPage: arr.length,
    type: prevType
  });
  return result;
};

export default function GenerateMaskModal({
  open,
  onClose,
  totalPages,
  pages,
  onGenerate,
}) {
  const [rows, setRows] = useState([
    { id: uuidv4(), fromPage: 1, toPage: 1, type: '' },
  ]);



  useEffect(() => {
    if (!pages?.length) return [];
    const sorted = pages;
    const result = buildRanges(sorted);
    setRows(result);
  }, [pages, open]);

  const usedTypes = useMemo(
    () => new Set(rows.map((r) => r.type).filter(Boolean)),
    [rows]
  );

  const canGenerate = useMemo(
    () => validateFullCoverage(rows, totalPages),
    [rows, totalPages]
  );

  const pageOptions = useMemo(() => {
    const n = totalPages || 0;
    return Array.from({ length: n }, (_, i) => i + 1);
  }, [totalPages]);

  const handleAddRow = () => {
    if (!totalPages) return;
    setRows((prev) => {
      const free = freeIntervalsAllRows(prev, totalPages);
      if (!free.length) return prev;
      const [s, e] = free[0];
      const next = [
        ...prev,
        {
          id: uuidv4(),
          fromPage: s,
          toPage: e,
          type: '',
        },
      ];
      return clampAllRows(next, totalPages);
    });
  };

  const handleRemoveRow = (id) => {
    setRows((prev) => {
      if (prev.length <= 1) return prev;
      const idx = prev.findIndex((r) => r.id === id);
      // if (idx !== prev.length - 1) return prev;
      const filtered = prev.filter((r) => r.id !== id);
      return clampAllRows(filtered, totalPages);
    });
  };
  const updateRow = (id, field, value) => {
    setRows((prev) => {
      const idx = prev.findIndex((r) => r.id === id);
      if (idx < 0) return prev;
      const next = [...prev];
      const cur = { ...next[idx] };
      if (field === 'fromPage') {
        const v = Number(value);
        cur.fromPage = v;
        if (cur.toPage < v) cur.toPage = v;
      } else if (field === 'toPage') {
        const v = Number(value);
        cur.toPage = v;
        if (cur.fromPage > v) cur.fromPage = v;
      } else {
        cur[field] = value;
      }
      next[idx] = cur;
      return clampAllRows(next, totalPages);
    });
  };

  const handleGenerate = () => {
    const updates = buildUpdates(rows, pages);
    if (updates.length && onGenerate) {
      onGenerate(updates);
      onClose();
    }
  };

  const addRangeDisabled =
    !totalPages || freeIntervalsAllRows(rows, totalPages).length === 0;

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '92vw', sm: 520 },
          maxHeight: '90vh',
          overflow: 'auto',
          bgcolor: 'background.paper',
          borderRadius: 1,
          boxShadow: 24,
          p: 2,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography variant="h6" component="h2" sx={{ pr: 2 }}>
            Generate mask or set mask for this e-book
          </Typography>
          <IconButton size="small" onClick={onClose} aria-label="close">
            <Close />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Total pages: {totalPages || 0}. Pages used in another range are disabled (greyed out) here. Example: range 10–50
          blocks 10–50; then you can pick 1–9 or 51–{totalPages || 0} next, or 1–25 then 26–50 in two rows (no overlap).
          Cover 1–{totalPages || 0} with no gaps. Each type once.
        </Typography>
        {rows.map((row, rowIndex) => {
          const otherUsed = collectUsedPages(rows, row.id);
          const fromVal = pageOptions.includes(row.fromPage) ? row.fromPage : pageOptions[0] ?? 1;
          const toVal = pageOptions.includes(row.toPage) ? row.toPage : pageOptions[pageOptions.length - 1] ?? 1;
          return (
            <Box
              key={row.id}
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.5,
                alignItems: 'flex-end',
                mb: 2,
                pb: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel id={`from-${row.id}`}>From Index</InputLabel>
                <Select
                  labelId={`from-${row.id}`}
                  label="From Index"
                  value={fromVal}
                  onChange={(e) => updateRow(row.id, 'fromPage', Number(e.target.value))}
                >
                  {pageOptions.map((p) => (
                    <MenuItem key={`from-${row.id}-${p}`} value={p} disabled={otherUsed.has(p)}>
                      {p}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 110 }}>
                <InputLabel id={`to-${row.id}`}>To Index</InputLabel>
                <Select
                  labelId={`to-${row.id}`}
                  label="To Index"
                  value={toVal}
                  onChange={(e) => updateRow(row.id, 'toPage', Number(e.target.value))}
                >
                  {pageOptions
                    .filter((p) => p >= row.fromPage)
                    .map((q) => (
                      <MenuItem
                        key={`to-${row.id}-${q}`}
                        value={q}
                        disabled={rangeTouchesOtherUsed(row.fromPage, q, otherUsed)}
                      >
                        {q}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel id={`type-${row.id}`}>Type</InputLabel>
                <Select
                  labelId={`type-${row.id}`}
                  label="Type"
                  value={row.type}
                  onChange={(e) => updateRow(row.id, 'type', e.target.value)}
                >
                  <MenuItem value="">
                    <em>Select</em>
                  </MenuItem>
                  {MASK_TYPES.map((t) => {
                    const takenElsewhere = usedTypes.has(t.value) && row.type !== t.value;
                    return (
                      <MenuItem key={t.value} value={t.value} disabled={takenElsewhere}>
                        {t.label}
                        {takenElsewhere ? ' (used)' : ''}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              {rows.length > 1 ? (
                <Button
                  size="small"
                  color="inherit"
                  onClick={() => handleRemoveRow(row.id)}
                  // disabled={rowIndex !== rows.length - 1}
                >
                  Remove
                </Button>
              ) : null}
            </Box>
          );
        })}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mt: 1 }}>
          <Button
            startIcon={<Add />}
            variant="outlined"
            size="small"
            onClick={handleAddRow}
            disabled={addRangeDisabled}
          >
            Add range
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button variant="contained" disabled={!canGenerate || !totalPages} onClick={handleGenerate}>
            Generate
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
